import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { Sale } from "../models/sale";
import { map } from "rxjs/operators";
import * as moment from "moment";
import { SALE_ORIGIN_XELT } from "../models/sale-constants";

@Injectable({
  providedIn: "root",
})
export class SalesStoreService {
  private readonly _sales = new BehaviorSubject<Sale[]>([]);
  private _activeTab: string = "sync";
  private _fromDate: Date = moment().clone().startOf("month").toDate();
  private _toDate: Date = moment().clone().endOf("month").toDate();
  private _selectedTeams: any[] = [];
  private _selectedSellers: any[] = [];

  constructor() {
    this.addSales([]);
  }

  private get sales(): Sale[] {
    return this._sales.getValue();
  }

  private set sales(val: Sale[]) {
    this._sales.next(val);
  }

  get activeTab() {
    return this._activeTab;
  }

  set activeTab(val: string) {
    this._activeTab = val;
  }

  get fromDate() {
    return this._fromDate;
  }

  set fromDate(val: Date) {
    this._fromDate = val;
  }

  get toDate() {
    return this._toDate;
  }

  set toDate(val: Date) {
    this._toDate = val;
  }

  get selectedTeams() {
    return this._selectedTeams;
  }

  set selectedTeams(val: any[]) {
    this._selectedTeams = val;
  }

  get selectedSellers() {
    return this._selectedSellers;
  }

  set selectedSellers(val: any[]) {
    this._selectedSellers = val;
  }

  addSales(sales: Sale[]) {
    this.sales = sales;
  }

  // Expose the observable$ part of the _sales subject (read only stream)
  readonly sales$ = this._sales.asObservable();

  // Stream of only synchronized sales
  readonly synchronizedSales$ = this.sales$.pipe(
    map(sales => sales.filter(sale => sale.status.toLowerCase().includes("sincronizado")))
  );

  // Stream of only approved sales
  readonly approvedSales$ = this.sales$.pipe(
    map(sales => sales.filter(sale => sale.status.toLowerCase().includes("aprovado")))
  );

  // Stream of only wait approvals sales
  readonly waitApprovalsSales$ = this.sales$.pipe(
    map(sales => sales.filter(sale => {
      if (sale.status.toLowerCase() !== "aguardando") return false;
      const hasContract = sale.statusAssinatura && sale.statusAssinatura.status;
      return !hasContract || sale.statusAssinatura.status === "signed";
    }))
  );

  // Stream of only errors sales
  readonly errorsSales$ = this.sales$.pipe(
    map(sales => sales.filter(sale => sale.status.toLowerCase().includes("erro")))
  );

  // Stream of only returned sales
  readonly returnedSales$ = this.sales$.pipe(
    map(sales => sales.filter(sale => sale.status.toLowerCase().includes("devolvido")))
  );

  // Stream of only returned sales
  readonly unpayedSales$ = this.sales$.pipe(
    map(sales => sales.filter(sale => sale.status.toLowerCase().includes("aguardando_pagamento")))
  );

  // Stream of only returned sales
  readonly unsignedSales$ = this.sales$.pipe(
    map(sales => sales.filter(sale => sale.status.toLowerCase().includes("aguardando_assinatura")))
  );

  // Stream of all sales (no filter)
  readonly allSales$ = this.sales$;
  readonly allSalesFromXelt$ = this.sales$.pipe(
    map(sales => sales.filter(sale => {
      if (sale.origin !== SALE_ORIGIN_XELT) return false;
      if (sale.statusAssinatura && sale.statusAssinatura.status === 'signed' && sale.status !== 'aguardando_pagamento') return false;
      const hasContract = sale.statusAssinatura && sale.statusAssinatura.status;
      if (!hasContract && sale.status && sale.status.toLowerCase() === 'aguardando') return false;
      return true;
    }))
  );
}
